import { z } from 'zod';

import {
  InterviewSlotAudienceSchema,
  InterviewSlotStatusSchema,
} from '../interview-slot/interview-slot';
import { InterviewMethodSchema } from '../interview/interview';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The widest window one read may ask for — a month grid with its spill-over
 * weeks is at most 42 days, and 62 leaves room for two months side by side.
 */
export const INTERVIEW_SLOT_WINDOW_MAX_DAYS = 62;

type Window = { from: string; to: string };

const endsAfterStart = (query: Window): boolean => Date.parse(query.to) > Date.parse(query.from);

const withinMaxDays = (query: Window): boolean =>
  Date.parse(query.to) - Date.parse(query.from) <= INTERVIEW_SLOT_WINDOW_MAX_DAYS * MS_PER_DAY;

const ENDS_AFTER_START = { message: 'The window must end after it starts.', path: ['to'] };
const WITHIN_MAX_DAYS = {
  message: `A window may span at most ${INTERVIEW_SLOT_WINDOW_MAX_DAYS} days.`,
  path: ['to'],
};

/**
 * `Z` only, never an offset. The client pins `from` once and sends it back for
 * every widen; an offset here is a 422 nothing in a suite would catch (P25e2's
 * trap, recorded before it is walked into a third time).
 */
const WindowShape = {
  from: z.iso.datetime(),
  to: z.iso.datetime(),
};

/** The staff calendar's read: every slot the window touches, optionally narrowed. */
export const InterviewSlotsListQuerySchema = z
  .object({
    ...WindowShape,
    audience: InterviewSlotAudienceSchema.optional(),
    method: InterviewMethodSchema.optional(),
    status: InterviewSlotStatusSchema.optional(),
  })
  .strict()
  .refine(endsAfterStart, ENDS_AFTER_START)
  .refine(withinMaxDays, WITHIN_MAX_DAYS);

export type InterviewSlotsListQuery = z.infer<typeof InterviewSlotsListQuerySchema>;

/**
 * The member's read: no filters at all. What a member may see is decided by
 * the server from their own scope (audience, lead time, capacity) — a filter
 * they could set would be a filter they could unset (N7).
 */
export const MemberInterviewSlotsListQuerySchema = z
  .object(WindowShape)
  .strict()
  .refine(endsAfterStart, ENDS_AFTER_START)
  .refine(withinMaxDays, WITHIN_MAX_DAYS);

export type MemberInterviewSlotsListQuery = z.infer<typeof MemberInterviewSlotsListQuerySchema>;
